import noop from 'lodash/noop'
import React, {
  FC,
  useContext,
  useEffect,
  useState,
} from 'react'
import styled from 'styled-components'

import CloseIcon from '@Assets/SVG/Close'
import { Image } from '@Components/Cloudinary/Image'
import { SVGIcon } from '@Components/Icons'
import { ShippingInfoModal } from '@Components/Shipping/ShippingInfoModal'
import { MetadataContext } from '@Globals/Metadata'
import { storageAvailable } from '@Globals/Storage'
import { up } from '@Globals/Style/breakpoints'
import {
  box,
  flexCenterChildren,
  flexCenterChildrenColumn,
  sectionInner,
  sectionOuter,
} from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'
import { UserContext } from '@Globals/User'

const YoloBarWrapper = styled.section`
  ${sectionOuter};
  background-color: ${({ theme }) => theme.colors.greyLightest};

  .inner {
    ${sectionInner}
    ${flexCenterChildrenColumn}
    position: relative;

    .messageWrap {
      padding: 10px 0;
      display: flex;
      align-items: center;
      justify-content: center;
      img {
        width: 60px;
        margin-right: 10px;
      }
      span, strong {
        max-width: 500px;
        font-size: 0.875rem;
        line-height: 1.5;
        ${up('desktop')} {
          line-height: 1.2;
        }
      }
      .shippingOptions {
        padding-left: 6px;
        cursor: pointer;
        text-decoration: underline;
      }
    }

    .closeIcon {
      ${flexCenterChildren};
      position: absolute;
      top: 0;
      right: 0;
      ${box({ height: '100%', width: '40px' })}
      padding: 0 40px 0 10px;
      ${up('desktop')} {
        padding: 13px;
      }
      cursor: pointer;
      svg {
        fill: ${({ theme }) => theme.colors.grey};
      }
      &:hover {
        svg {
          fill: ${({ theme }) => theme.colors.greyDark};
        }
      }
    }
  }
`

const LS_YOLO_EU_TOP_BAR_CLOSED = 'yolo-eu-top-bar-closed'

interface YOLOBarProps {
  onVisibilityChange?: () => void
}

export const YOLOBar: FC<YOLOBarProps> = (
  {
    onVisibilityChange = noop,
  }: YOLOBarProps,
) => {
  const { t, tDanger } = useTranslation()
  const { userData } = useContext(UserContext)
  const { store } = useContext(MetadataContext)

  const [showYOLOBar, setShowYOLOBar] = useState(false)
  const [shippingInfoModalOpen, setShippingInfoModalOpen] = useState(false)

  useEffect(() => {
    if (storageAvailable('sessionStorage') && window.sessionStorage.getItem(LS_YOLO_EU_TOP_BAR_CLOSED)) {
      return
    }

    if (!userData?.otherCountry?.isEU) {
      return
    }

    setShowYOLOBar(true)
  }, [userData?.otherCountry?.isEU])

  const onCloseButtonClick = () => {
    if (storageAvailable('sessionStorage')) {
      window.sessionStorage.setItem(LS_YOLO_EU_TOP_BAR_CLOSED, '1')
    }

    setShowYOLOBar(false)
  }

  useEffect(() => {
    onVisibilityChange()
  }, [onVisibilityChange, showYOLOBar])

  if (!showYOLOBar) {
    return null
  }

  const country = userData?.otherCountry?.countryNameMap[store?.id]
  return (
    <>
      <YoloBarWrapper>
        <div className="inner">
          <div className="messageWrap">
            <Image
              alt="Trendhim EU shipping"
              options={{
                path: 'frontend/random-images/eu-truck.svg',
              }}
            />
            <span>
              <span {...tDanger('header:YOLO_TOP_BAR_EU_SHIPPING_TEXT', { arg1: country })} />
              <button className="shippingOptions" onClick={() => setShippingInfoModalOpen(true)} type="button">
                {t('header:SEE_SHIPPING_OPTIONS')}
              </button>
            </span>
          </div>
          <div className="closeIcon" onClick={onCloseButtonClick} role="button" tabIndex={0}>
            <SVGIcon icon={CloseIcon} size="14px" />
          </div>
        </div>
      </YoloBarWrapper>

      <ShippingInfoModal
        isOpen={shippingInfoModalOpen}
        onHide={() => setShippingInfoModalOpen(false)}
        sourceID="yolo_header_bar"
      />
    </>
  )
}
